import { useCallback, useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { IPermission, IRole } from "../../../../interfaces";
import PermissionsService from "../../../../services/http/services/PermissionsService";

export default function useRoleForm(
    onSubmit: (role: IRole) => Promise<void>,
    role: IRole | undefined
) {
    const [isSubmiting, setIsSubmiting] = useState<boolean>(false);
    const [isLoadingPermissions, setIsLoadingPermissions] = useState<boolean>(true);
    const [permissions, setPermissions] = useState<Array<IPermission>>([]);

    const {
        handleSubmit,
        formState: { errors },
        control,
        setValue,
        watch,
        clearErrors,
    } = useForm<IRole>({
        defaultValues: {
            name: role?.name || "",
            description: role?.description || "",
            permission_ids:
                role?.permissions
                    ?.map((permission) => permission.id)
                    .filter((id) => typeof id === "number") || [],
        },
    });

    const watchedPermissionIds = watch("permission_ids") || [];

    const handleSubmitForm: SubmitHandler<IRole> = async (data) => {
        setIsSubmiting(true);
        try {
            await onSubmit(data);
        } finally {
            setIsSubmiting(false);
        }
    };

    const handleSelectAllPermissions = useCallback(() => {
        const allIds = permissions.map((p) => p.id).filter((id): id is number => typeof id === "number");
        setValue("permission_ids", allIds, { shouldValidate: true });
        clearErrors("permission_ids");
    }, [permissions, setValue, clearErrors]);

    const handleClearAllPermissions = useCallback(() => {
        setValue("permission_ids", [], { shouldValidate: true });
    }, [setValue]);

    useEffect(() => {
        const controller = new AbortController();

        async function loadPermissions() {
            try {
                setIsLoadingPermissions(true);
                const permissionsList = await PermissionsService.listPermissions(controller.signal);
                setPermissions(permissionsList);

                if (role?.permissions && role.permissions.length > 0) {
                    const ids = role.permissions
                        .map((p) => p.id)
                        .filter((id): id is number => typeof id === "number");
                    setValue("permission_ids", ids);
                }
            } catch {
                // Ignore abort errors
            } finally {
                setIsLoadingPermissions(false);
            }
        }

        loadPermissions();

        return () => {
            controller.abort();
        };
    }, [role, setValue]);

    return {
        isLoadingPermissions,
        permissions,
        handleSubmitForm,
        handleSubmit,
        control,
        isSubmiting,
        errors,
        watchedPermissionIds,
        handleSelectAllPermissions,
        handleClearAllPermissions,
    };
}